import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import clsx from 'clsx';
import { Platforms } from '@/common/constants';
import { useIframeResize, usePlatform } from '@/common/hooks';
import { getIsInIframe } from '@/common/utils';
import { HomeIndicator, TabsSkeleton } from '@/ui/components';
import { MainMenu } from '../../components';
import { MainMenuOptionValues } from '../../constants';
import { usePrematchData } from '../../hooks';
import type { MainMenuOption } from '../../types';
import StatisticsTab from '../StatisticsTab/StatisticsTab';
import TableTab from '../TableTab/TableTab';
import PlayersTab from '../PlayersTab/PlayersTab';
import LineupTab from '../LineupTab/LineupTab';

const TABS: Record<MainMenuOption, ComponentType> = {
  [MainMenuOptionValues.STATS]: StatisticsTab,
  [MainMenuOptionValues.TABLE]: TableTab,
  [MainMenuOptionValues.PLAYERS]: PlayersTab,
  [MainMenuOptionValues.LINEUP]: LineupTab,
};

export interface PrematchWidgetContentProps {
  activeTab: MainMenuOption;
  onActiveTabChange: (tab: MainMenuOption) => void;
}

export default function PrematchWidgetContent({
  activeTab,
  onActiveTabChange,
}: PrematchWidgetContentProps) {
  const [isLoadingFirstTime, setIsLoadingFirstTime] = useState(true);
  const isInIframe = getIsInIframe();

  const { teams, statisticsStatus, tableStatus, playersStatus, lineupStatus } = usePrematchData();
  const platform = usePlatform();
  const { containerRef } = useIframeResize();

  useEffect(() => {
    if (activeTab === MainMenuOptionValues.LINEUP && !teams.lineupAvailable) {
      onActiveTabChange(MainMenuOptionValues.STATS);
    }
  }, [activeTab, teams.lineupAvailable, onActiveTabChange]);

  const isAnyTabResolved =
    statisticsStatus !== 'pending' ||
    tableStatus !== 'pending' ||
    playersStatus !== 'pending' ||
    lineupStatus !== 'pending';

  if (isLoadingFirstTime && isAnyTabResolved) {
    setIsLoadingFirstTime(false);
  }

  function renderMainMenu() {
    if (isLoadingFirstTime) {
      return <TabsSkeleton className="px-[12px] pt-[6px] pb-[8px]" />;
    }

    return <MainMenu activeTab={activeTab} onActiveTabChange={onActiveTabChange} />;
  }

  const TabContent = TABS[activeTab];

  const headerBackgroundClassName = isLoadingFirstTime
    ? 'bg-pmw-sb-secondary-light dark:bg-base120 z-10'
    : 'bg-pmw-sb-primary-light dark:bg-pmw-sb-primary-dark z-10';

  return (
    <div
      ref={containerRef}
      className={clsx(
        'flex flex-col',
        (platform === Platforms.MOBILE || !isInIframe) && 'h-full',
        platform === Platforms.MOBILE && 'bg-pmw-sb-secondary-light dark:bg-base120',
      )}
    >
      <div className={headerBackgroundClassName}>
        {platform === Platforms.MOBILE && <HomeIndicator />}
        {renderMainMenu()}
      </div>
      <div className="z-0 flex-1 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <TabContent />
      </div>
    </div>
  );
}
